import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthModel } from '@auth/models';
import { StorageService } from '@core/services/storage.service';
import { ThemeConfigService } from '@core/services/theme-config.service';
import { AccountDetailsDTO } from '@features/accounts/shared/dtos/account-details.dto';
import { LoginPayloadDTO, MenuLoginDTO } from '@features/login/dtos';
import { jwtDecode } from 'jwt-decode';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

import { AccessControlService } from './access-control.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private loggedIn$: BehaviorSubject<boolean>;
  private userMenus$: BehaviorSubject<MenuLoginDTO[]>;
  private userAccount$: BehaviorSubject<AccountDetailsDTO | null>;

  private readonly LOGGED_USER_KEY = 'logged_user';

  constructor(
    private router: Router,
    private storage: StorageService,
    private themeConfig: ThemeConfigService,
    private accessControlService: AccessControlService,
  ) {
    this.loggedIn$ = new BehaviorSubject<boolean>(false);
    this.userMenus$ = new BehaviorSubject<MenuLoginDTO[]>([]);
    this.userAccount$ = new BehaviorSubject<AccountDetailsDTO | null>(null);

    const isLoggedIn = !!this.storage.getItem(this.LOGGED_USER_KEY);

    if (isLoggedIn) {
      this.loadAccessControl();
      this.loadUserMenus();
      this.loadUserAccountFromToken();
    }
  }

  private getTokenPayload(): LoginPayloadDTO | null {
    const user = this.getLoggedUser();
    if (!user?.token) return null;

    try {
      return jwtDecode<LoginPayloadDTO>(user.token);
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }

  private loadUserAccountFromToken(): void {
    const payload = this.getTokenPayload();

    if (payload?.account) {
      this.userAccount$.next(payload.account);
    } else {
      this.userAccount$.next(null);
    }
  }

  private loadUserMenus(): void {
    const user = this.getLoggedUser();
    if (!user?.menus) return;

    try {
      this.userMenus$.next(user.menus);
    } catch (error) {
      console.error('Erro ao carregar menus do usuário:', error);
    }
  }

  private loadAccessControl(): void {
    const user = this.getLoggedUser();
    if (!user?.menus) return;

    this.accessControlService.load(user);
  }

  getCompanyLogo(): string | null {
    const user = this.getLoggedUser();
    return user?.companyLogo || null;
  }

  getUserProfilePicture(): string | null {
    const payload = this.getTokenPayload();
    return payload?.profilePictureUrl || this.getLoggedUser()?.photo || null;
  }

  authenticate(auth: AuthModel): void {
    this.accessControlService.load(auth);
    this.themeConfig.setTheme({});
    this.loggedIn$.next(true);
    this.updateLoggedUser(auth);
    this.loadUserMenus();
    this.loadUserAccountFromToken();
  }

  logout(): void {
    this.accessControlService.clear();
    this.storage.removeItem(this.LOGGED_USER_KEY);
    this.userMenus$.next([]);
    this.loggedIn$.next(false);
    this.userAccount$.next(null);
    this.router.navigate(['/login']);
  }

  isLoggedIn$(): Observable<boolean> {
    if (!this.loggedIn$.getValue()) {
      const token = this.storage.getItem(this.LOGGED_USER_KEY);
      this.loggedIn$.next(!!token);
    }

    return this.loggedIn$.asObservable();
  }

  getToken(): string | null {
    return this.getLoggedUser()?.token ?? null;
  }

  getUserAccount(): Observable<AccountDetailsDTO | null> {
    return this.userAccount$.asObservable();
  }

  getUserMenus(): Observable<MenuLoginDTO[]> {
    return this.userMenus$.asObservable();
  }

  isLogged(): boolean {
    return !!this.getLoggedUser();
  }

  hasRole(role: string): boolean {
    const user = this.getTokenPayload();
    if (!user?.role) return false;

    const roles = Array.isArray(user.role) ? user.role : [user.role];

    return roles.includes(role);
  }

  isSuperAdmin(): boolean {
    return this.hasRole('SADMIN');
  }

  getLoggedUser(): AuthModel | null {
    const user = this.storage.getItem(this.LOGGED_USER_KEY);
    if (!user) {
      return null;
    }

    return JSON.parse(user);
  }

  getTokenExpirationDate(): Date | null {
    const user = this.getLoggedUser();
    if (!user?.token) {
      return null;
    }

    const payload = this.getTokenPayload();
    if (!payload?.exp) {
      return null;
    }

    return new Date(payload.exp * 1000);
  }

  updateLoggedUser(auth: AuthModel) {
    this.storage.setItem(this.LOGGED_USER_KEY, JSON.stringify(auth));
  }
}
